import React, { useState } from 'react';
import { X, Printer } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Supplier } from '../types';
import Modal from './Modal';

interface ShippingLabelModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplier: Supplier | null;
}

const ShippingLabelModal: React.FC<ShippingLabelModalProps> = ({ isOpen, onClose, supplier }) => {
  const { user } = useAuth();
  const [labelData, setLabelData] = useState({
    content: ''
  });
  const [companyName, setCompanyName] = useState('IT Toolbox');
  const [labelSettings, setLabelSettings] = useState({
    width: 105,
    height: 74,
    orientation: 'horizontal',
    margin_top: 4,
    margin_right: 4,
    margin_bottom: 4,
    margin_left: 4
  });

  React.useEffect(() => {
    if (isOpen) {
      fetch('/api/settings')
        .then(res => res.json())
        .then(data => {
          if (data.company_name) {
            setCompanyName(data.company_name);
          }
        })
        .catch(err => console.error('Settings fetch error:', err));
    }
  }, [isOpen]);

  if (!isOpen || !supplier) return null;

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Kargo Etiketi</title>
          <style>
            @page {
              size: ${labelSettings.orientation === 'vertical' ? labelSettings.height : labelSettings.width}mm ${labelSettings.orientation === 'vertical' ? labelSettings.width : labelSettings.height}mm;
              margin: 0;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: ${labelSettings.margin_top}mm ${labelSettings.margin_right}mm ${labelSettings.margin_bottom}mm ${labelSettings.margin_left}mm;
              width: ${labelSettings.orientation === 'vertical' ? labelSettings.height : labelSettings.width}mm;
              height: ${labelSettings.orientation === 'vertical' ? labelSettings.width : labelSettings.height}mm;
              box-sizing: border-box;
              font-size: 8px;
              line-height: 1.1;
            }
            .label {
              border: 2px solid #000;
              height: 100%;
              display: flex;
              flex-direction: column;
              padding: 4px;
            }
            .header {
              text-align: center;
              font-weight: bold;
              font-size: 12px;
              border-bottom: 1px solid #000;
              padding-bottom: 4px;
              margin-bottom: 4px;
            }
            .section {
              margin-bottom: 6px;
              flex: 1;
            }
            .section-title {
              font-weight: bold;
              font-size: 9px;
              margin-bottom: 2px;
              text-transform: uppercase;
            }
            .address {
              border: 1px solid #000;
              padding: 3px;
              min-height: 15mm;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 2px;
            }
            .barcode {
              text-align: center;
              font-family: 'Courier New', monospace;
              font-size: 8px;
              margin-top: 4px;
              border-top: 1px solid #000;
              padding-top: 4px;
            }
          </style>
        </head>
        <body>
          <div class="label">
            <div class="header">KARGO ETİKETİ - ${companyName}</div>
            
            <div class="section">
              <div class="section-title">Gönderen:</div>
              <div class="address">
                <strong>${user?.name || ''}</strong><br>
                ${user?.phone || user?.company?.phone || ''}<br>
                ${user?.address || user?.company?.address || 'IT Toolbox Sistemi'}<br>
                ${user?.email || ''}
              </div>
            </div>
            
            <div class="section">
              <div class="section-title">Alıcı:</div>
              <div class="address">
                <strong>${supplier.name}</strong><br>
                ${supplier.contact_person}<br>
                ${supplier.phone}<br>
                ${supplier.email}<br>
                ${supplier.address}
              </div>
            </div>
            
            <div class="section">
              <div class="info-row">
                <span><strong>İçerik:</strong> ${labelData.content || 'Belirtilmemiş'}</span>
              </div>
              <div class="info-row">
                <span><strong>Tarih:</strong> ${new Date().toLocaleDateString('tr-TR')}</span>
              </div>
            </div>
            
            <div class="barcode">
              *** ${Date.now().toString().slice(-8)} ***
            </div>
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {supplier && (
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Kargo Etiketi - {supplier.name}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Kargo Bilgileri</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                İçerik
              </label>
              <input
                type="text"
                value={labelData.content}
                onChange={(e) => setLabelData({...labelData, content: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Örn: Elektronik malzeme"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Etiket Boyutları
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Genişlik (mm)
                  </label>
                  <input
                    type="number"
                    min="80"
                    max="150"
                    value={labelSettings.width}
                    onChange={(e) => setLabelSettings({...labelSettings, width: parseInt(e.target.value) || 105})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Yükseklik (mm)
                  </label>
                  <input
                    type="number"
                    min="50"
                    max="200"
                    value={labelSettings.height}
                    onChange={(e) => setLabelSettings({...labelSettings, height: parseInt(e.target.value) || 74})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={() => setLabelSettings({...labelSettings, width: 105, height: 74})}
                  className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                >
                  105x74mm
                </button>
                <button
                  onClick={() => setLabelSettings({...labelSettings, width: 100, height: 150})}
                  className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                >
                  100x150mm
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Etiket Yönü
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="horizontal"
                    checked={labelSettings.orientation === 'horizontal'}
                    onChange={(e) => setLabelSettings({...labelSettings, orientation: e.target.value})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Yatay</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="vertical"
                    checked={labelSettings.orientation === 'vertical'}
                    onChange={(e) => setLabelSettings({...labelSettings, orientation: e.target.value})}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Dikey</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kenar Boşlukları (mm)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Üst
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="15"
                    value={labelSettings.margin_top}
                    onChange={(e) => setLabelSettings({...labelSettings, margin_top: parseInt(e.target.value) || 4})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Sağ
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="15"
                    value={labelSettings.margin_right}
                    onChange={(e) => setLabelSettings({...labelSettings, margin_right: parseInt(e.target.value) || 4})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Alt
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="15"
                    value={labelSettings.margin_bottom}
                    onChange={(e) => setLabelSettings({...labelSettings, margin_bottom: parseInt(e.target.value) || 4})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Sol
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="15"
                    value={labelSettings.margin_left}
                    onChange={(e) => setLabelSettings({...labelSettings, margin_left: parseInt(e.target.value) || 4})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </div>
            

          </div>
          
          {/* Preview */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Etiket Önizleme</h3>
            
            <div className="border-2 border-gray-300 bg-white" style={{width: `${Math.min(labelSettings.width * 1.7, 180)}px`, height: `${Math.min(labelSettings.height * 1.6, 120)}px`, fontSize: '8px'}}>
              <div className="border-2 border-black h-full flex flex-col p-1">
                <div className="text-center font-bold text-xs border-b border-black pb-1 mb-2">
                  KARGO ETİKETİ - {companyName}
                </div>
                
                <div className="mb-2">
                  <div className="font-bold text-[8px] mb-1">GÖNDEREN:</div>
                  <div className="border border-black p-1 text-[7px] min-h-[18px]">
                    <strong>{user?.name || ''}</strong><br/>
                    {(user as any)?.phone || (user as any)?.company?.phone || ''}<br/>
                    {(user as any)?.address || (user as any)?.company?.address || 'IT Toolbox Sistemi'}<br/>
                    {user?.email || ''}
                  </div>
                </div>
                
                <div className="mb-2 flex-1">
                  <div className="font-bold text-[8px] mb-1">ALICI:</div>
                  <div className="border border-black p-1 text-[7px] min-h-[25px]">
                    <strong>{supplier.name}</strong><br/>
                    {supplier.contact_person}<br/>
                    {supplier.phone}<br/>
                    {supplier.email}<br/>
                    <div className="text-[6px]">{supplier.address}</div>
                  </div>
                </div>
                
                <div className="mb-2">
                  <div className="text-[7px] mb-1">
                    <strong>İçerik:</strong> {labelData.content || 'Belirtilmemiş'}
                  </div>
                  <div className="text-[7px]">
                    <strong>Tarih:</strong> {new Date().toLocaleDateString('tr-TR')}
                  </div>
                </div>
                
                <div className="text-center text-[6px] border-t border-black pt-1">
                  *** {Date.now().toString().slice(-8)} ***
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            İptal
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            <Printer className="w-4 h-4 mr-2" />
            Yazdır
          </button>
        </div>
      </div>
      )}
    </Modal>
  );
};

export default ShippingLabelModal;